import { put, takeEvery } from 'redux-saga/effects';

import { resourceSagaUtils, resourceStoreTypes } from 'app-components/flux/core';
import type { ResourceActionCreator, ResourceFailureActionCreator } from 'app-components/flux/core/resourceStoreTypes';
import { datasetsActions } from 'app-components/flux/datasets';

import * as actions from './categoriesActions';
import { getLastSyncDate } from './categoriesSelectors';
import * as categoriesTransformers from './categoriesTransformers';
import type { CategoryProps } from './categoriesTypes';

export function* categoriesDumpUpdater(resources: CategoryProps[]) {
  const categories = resources.map(categoriesTransformers.transformServerResourceToClientResource);
  yield put(actions.getCategoriesSuccess({ resources: categories }, { context: 'BUMP_CHANGE_DUMP' }));
}

export function* categoriesNudgeUpdater() {
  yield put(actions.getCategories(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
}

const mkQcsSyncCategoriesConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<CategoryProps>();

const mkResourceConfig = (
  successAction: ResourceActionCreator<CategoryProps>,
  failureAction: ResourceFailureActionCreator,
) =>
  mkQcsSyncCategoriesConfig({
    resourceName: 'category',
    resourceBaseUrl: '/categories',
    getLastSyncDate,
    applyResourceFromCRUDResponse: true,

    transformClientResourceToRequestData: categoriesTransformers.transformClientResourceToRequestData,
    transformServerResourceToClientResource: categoriesTransformers.transformServerResourceToClientResource,

    successAction,
    failureAction,
  });

export function* getCategoriesActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.getCategoriesSuccess, actions.getCategoriesFailure);
  yield resourceSagaUtils.takeAndDebounce(
    actions.getCategories,
    resourceSagaUtils.qcsSyncGetResources<CategoryProps>,
    resourceConfig,
  );
}

export function* createCategoryActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.createCategorySuccess, actions.createCategoryFailure);
  yield takeEvery(actions.createCategory.type, resourceSagaUtils.qcsSyncCreateResource<CategoryProps>, resourceConfig);
}

export function* updateCategoryActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.updateCategorySuccess, actions.updateCategoryFailure);
  yield takeEvery(actions.updateCategory.type, resourceSagaUtils.qcsSyncUpdateResource<CategoryProps>, resourceConfig);
}

export function* batchCategoriesActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.batchCategoriesSuccess, actions.batchCategoriesFailure);
  yield takeEvery(
    actions.batchCategories.type,
    resourceSagaUtils.qcsSyncUpdateResources<CategoryProps>,
    resourceConfig,
  );
}

export function* deleteCategoryActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.deleteCategorySuccess, actions.deleteCategoryFailure);
  yield takeEvery(actions.deleteCategory.type, resourceSagaUtils.qcsSyncDeleteResource<CategoryProps>, resourceConfig);
}

function* datasetPollSuccessActionWatcher() {
  yield takeEvery(datasetsActions.datasetPollSuccess, function* getCategories() {
    yield put(actions.getCategories());
  });
}

function* deleteAndReassignCategoryActionWatcher() {
  yield takeEvery(actions.deleteAndReassignCategory, resourceSagaUtils.qcsRequest, {
    method: 'PUT',
    path: (action: ReturnType<typeof actions.deleteAndReassignCategory>) =>
      `/categories/delete-and-reassign/${action.payload.id}`,
    data: (action: ReturnType<typeof actions.deleteAndReassignCategory>) => action.payload.coa,
    successAction: actions.deleteAndReassignCategorySuccess,
    failureAction: actions.deleteAndReassignCategoryFailure,
  });
}

// ====================================================
// EXPORTS

export default [
  getCategoriesActionWatcher,
  createCategoryActionWatcher,
  updateCategoryActionWatcher,
  batchCategoriesActionWatcher,
  deleteCategoryActionWatcher,
  datasetPollSuccessActionWatcher,
  deleteAndReassignCategoryActionWatcher,
];
